import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  Input,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Property } from 'app/modules/projects/projects.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { AddressPart, parsePlace } from 'app/shared/models/address';
import { AddressBase, CustomAddressFieldValue } from '../../custom-form.types';
import { GoogleServiceHub } from 'app/services/google.service';
import { RelatedToType } from 'app/shared/enums';
import { Dictionary } from 'app/shared/models';

type GoogleResult = { results: { description: string; place_id: string }[]; status: 'OK' | 'ZERO_RESULTS' };
type PropertyPlaceSuggestion = Property & AddressBase & { place_id?: string };
type SegmentedSuggestion = {
  existing_properties: PropertyPlaceSuggestion[];
  new_properties: PropertyPlaceSuggestion[];
};

@Component({
  selector: 'app-custom-address',
  templateUrl: './custom-address.component.html',
  styleUrls: ['./custom-address.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomAddressComponent extends OnDestroyMixin implements OnInit, OnChanges {
  @Input() mode: 'default' | 'searchSimilar' = 'default';
  @Input() relatedToType: RelatedToType;
  @Input() setValues: Dictionary<any>;
  @Input() readonly: boolean;

  @Output() onNavigateByUrl = new EventEmitter<string>();
  @Output() onValueChanged = new EventEmitter<CustomAddressFieldValue>();
  @Output() onToggleLoading = new EventEmitter<void>();

  @ViewChild('streetInput', {}) streetInput: ElementRef<HTMLInputElement>;

  form = new UntypedFormGroup({
    street: new UntypedFormControl(null),
    city: new UntypedFormControl(null),
    state: new UntypedFormControl(null),
    postal_code: new UntypedFormControl(null),
    latitude: new UntypedFormControl(null),
    longitude: new UntypedFormControl(null),
    id: new UntypedFormControl(null),
    place_id: new UntypedFormControl(null),
  });

  private lastAddressSelected: AddressBase = null;

  googleResults = new Subject<GoogleResult>();
  selectedGoogleResult = new Subject<any>();
  isLoading = false;

  segmentedSuggestions: SegmentedSuggestion = { existing_properties: [], new_properties: [] };

  get streetControl(): AbstractControl {
    return this.form.get('street');
  }

  constructor(
    private readonly propertiesService: PropertiesService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly googleServiceHub: GoogleServiceHub,
  ) {
    super();
  }

  private isDifferentFromLast(address: AddressBase): boolean {
    return (
      this.lastAddressSelected.city !== address.city ||
      this.lastAddressSelected.postal_code !== address.postal_code ||
      this.lastAddressSelected.state !== address.state ||
      this.lastAddressSelected.street !== address.street
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.setValues?.currentValue?.address) {
      const addressData = changes.setValues.currentValue.address;
      if (addressData) {
        const formValue = {
          street: addressData.street || null,
          city: addressData.city || null,
          state: addressData.state || null,
          postal_code: addressData.postal_code || null,
          latitude: addressData.latitude || null,
          longitude: addressData.longitude || null,
          id: addressData.id || null,
          place_id: addressData.place_id || null,
        };
        this.form.setValue(formValue);
      }
    }
  }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        debounceTime(250),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe((value) => {
        const { id, place_id, street, city, state, postal_code, latitude, longitude } = value;
        let _id = id;

        if (id && this.isDifferentFromLast(value)) {
          _id = null;
        }

        this.onValueChanged.emit(
          // Avoid sending address object nested in property attribute when creating a property from properties table.
          this.relatedToType === RelatedToType.Property
            ? {
                address: { id: _id, place_id, street, city, state, postal_code, latitude, longitude },
              }
            : {
                property: {
                  address: { id: _id, place_id, street, city, state, postal_code, latitude, longitude },
                },
              },
        );
      });

    this.streetControl.valueChanges
      .pipe(
        debounceTime(500),
        filter((query) => query),
        tap((query: string) => {
          this.isLoading = true;
          this.segmentedSuggestions = { existing_properties: [], new_properties: [] } as SegmentedSuggestion;

          this.changeDetectorRef.detectChanges();

          const request = { input: query };
          this.googleServiceHub.autocompleteService.getPlacePredictions(
            request,
            ((results, status) => this.googleResults.next({ results, status })).bind(this),
          );
        }),
        switchMap((query: string) => {
          return combineLatest([
            this.propertiesService
              .getPropertiesWithoutSort(
                ['id', 'name', 'address_string', 'postal_code', 'city', 'street_address', 'state'],
                {},
                0,
                10,
                query,
                '',
                '',
                false,
              )
              .pipe(map((result) => result.properties)),
            this.googleResults.asObservable(),
          ]);
        }),
      )
      .subscribe(([ourProperties, suggestionsFromGoogle]: [PropertyPlaceSuggestion[], GoogleResult]) => {
        let results: PropertyPlaceSuggestion[] = ourProperties;

        if (suggestionsFromGoogle.status === 'OK') {
          results = [
            ...results,
            ...suggestionsFromGoogle.results.map(
              ({ description, place_id }) =>
                ({
                  place_id,
                  address_string: description,
                }) as PropertyPlaceSuggestion,
            ),
          ];
        }

        this.segmentedSuggestions = {
          existing_properties: results.filter((suggestion) => suggestion.id),
          new_properties: results.filter((suggestion) => !suggestion.id),
        };

        this.isLoading = false;

        this.changeDetectorRef.detectChanges();
      });
  }

  selectOption(property: Partial<PropertyPlaceSuggestion>): void {
    if (property.place_id) {
      const request = {
        placeId: property.place_id,
        fields: ['name', 'formatted_address', 'place_id', 'address_components', 'geometry'],
      };

      this.googleServiceHub.placesService.getDetails(request, (result, status: 'OK') => {
        if (status === 'OK') {
          const parsedAddress = parsePlace(result);

          this.onSelectAddressParts(parsedAddress, { placeId: property.place_id });
        }
      });
    } else {
      this.onSelectAddressParts(
        {
          locality: property.city,
          route: property.street_address,
          postal_code: property.postal_code,
          administrative_area_level_1: property.state,
        },
        { id: property.id },
      );
    }

    this.segmentedSuggestions = { existing_properties: [], new_properties: [] } as SegmentedSuggestion;

    setTimeout(() => this.streetInput.nativeElement.blur(), 0);
  }

  onSelectAddressParts(addressPart: Partial<AddressPart>, idOrPlaceId: { id?: number; placeId?: string } = null): void {
    const { locality, administrative_area_level_1, street_number, route, postal_code, lat, lng, sublocality_level_1 } =
      addressPart;

    const address = {
      city: locality ?? sublocality_level_1 ?? null,
      state: administrative_area_level_1 ?? null,
      postal_code: postal_code ?? null,
      latitude: lat ?? null,
      longitude: lng ?? null,
      id: idOrPlaceId.id ?? null,
      place_id: idOrPlaceId.placeId ?? null,
      street: route ? (street_number ? `${street_number} ${route}` : route) : null,
    };

    this.lastAddressSelected = { ...address };

    this.form.setValue(address);
  }
}
